import lodash from 'lodash';
import { DateTime } from 'luxon';
import { memoizeWithObjectKey } from 'utils/memoizeWithKey';

import type { CashFlowTransaction } from 'companion-app-components/flux/transactions/transactionsTypes';
import { transactionsUtils } from 'companion-app-components/flux/transactions';

import { isUnacceptedScheduledTxn, getTxnStateInfo, isNextScheduledInstance } from 'data/transactions/utils';
import { filterByKeys, findInObject, capitalize } from 'utils/utils';
import { getFieldString, filterTransactionsByExpression, filterTransactionsByFilterObject } from 'data/transactions/searchFilter';
import * as transactionsConfig from './transactionsConfig';

export const fieldIsMoveable = (field: string) => {
  const items = findInObject(transactionsConfig.fieldData, (x, k) => k === field);
  if (items.length > 0) {
    return items[0].moveable;
  }

  return false;
};

export const fieldIsEditable = (txn: CashFlowTransaction, fld: string, props: Record<string, any>) => {
  const stateInfo = getTxnStateInfo(txn);
  const skipAmounts = (txn.cpData && !props.editDownloaded) || props.showSplitWindow;
  let fieldNotActive = !props.headerFields.includes(fld) ||
    !props.regEditableFields.includes(fld) ||
    (fld === 'account' && (props.accountIds.size <= 1 ||
      (stateInfo.entry === 'bank' || stateInfo.entry === 'matched'))) ||
    (fld === 'balance' && (!props.editRunningBalance || props.accountIds.size > 1)) ||
    (!props.txnCancelExtraColumn && fld === 'close') ||
    (props.showAccountColors && fld === 'account') ||
    (isUnacceptedScheduledTxn(txn) && transactionsConfig.recurringEditable.indexOf(fld) < 0) ||
    (fld === 'select' && stateInfo.status === 'UPCOMING' && !isNextScheduledInstance(txn, props.scheduledTransactionsById)) ||
    (!props.showAccountColors && fld === 'accountColor');

  fieldNotActive = fieldNotActive || (skipAmounts && (fld === 'amount' || fld === 'income' || fld === 'expense')) ||
    (transactionsUtils.isSplitTxn(txn) && (fld === 'tags' || fld === 'category')) || (props.showSplitWindow && fld === 'category');

  // Condition for account not synced case
  if ((fld === 'menu' || fld === 'state') && txn?.coa?.type === 'ACCOUNT' && props.allAccountIds && !props.allAccountIds.includes(txn.coa.id)) {
    fieldNotActive = true;
  } 

  return !fieldNotActive;
};

const createCSVRow = (regColumns: Record<string, any>, txn: CashFlowTransaction, longCats: boolean) => {
  const row: string[] = [];
  regColumns.forEach((field) => {
    row.push(`"${getFieldString(field, txn, longCats)}"`);
  });
  return row;
};

const createCSVSplitRow = (regColumns: Record<string, any>, txn: CashFlowTransaction, longCats: boolean) => {
  const row: string[] = [];
  regColumns.forEach((field) => {
    if (field === 'category' || field === 'amount') {
      row.push(`"${getFieldString(field, txn, longCats)}"`);
    } else {
      row.push('');
    }
  });
  return row;
};

const processTxnList = (props, headers, data, txnList) => {
  txnList.forEach((txn) => {
    data.push(createCSVRow(headers, txn, props.longCats));
    if (transactionsUtils.isSplitTxn(txn)) {
      txn.split.items.forEach((item) => {
        data.push(createCSVSplitRow(headers, item, props.longCats));
      });
    }
  });
};

const convertRegViewToArrays = (props) => {
  const data = [];
  const headers = Object.keys(filterByKeys(transactionsConfig.fieldData, (x) => x.exportable));
  const idxNotes = headers.indexOf('notes');
  if (idxNotes !== -1) {
    headers[idxNotes] = 'memo';
  }
  // Per Kelly J, if there are selected transactions, download those, otherwise
  // download all the current acctTxns
  if (props.uiState.selectedTxns && props.uiState.selectedTxns.size > 0) {
    processTxnList(props, headers, data, props.uiState.selectedTxns);
  } else {
    props.acctTxns.forEach((section) => {
      // const shortKey = key.slice(key.indexOf(':') + 1);
      // if (props.uiState.openSections.has(shortKey)) {
      processTxnList(props, headers, data, section);
      // }
    });
  }

  return { headers, data };
};

const convertToFile = (headers, data) => {
  // This special prepend is a flag to microsoft EXCEL to use UTF-8 in importing
  // which is required for currency symbols that are multi-byte
  let str = '\uFEFF';
  headers.forEach((x) => { str = `${str},${x}`; });
  str = `${str}\n`;
  data.forEach((x) => {
    x.forEach((y) => {
      str = `${str},${y}`;
    });
    str = `${str}\n`;
  });

  return new Blob([str], { type: 'text/csv; charset=utf-8' });
};

// Download the current view to a CSV file
export const downloadRegisterToCSV = (props) => {
  const { headers, data } = convertRegViewToArrays(props);
  const filename = `${capitalize(props?.dataset?.name)}_transactions_${DateTime.now().toFormat('ddMMMyy')}.csv`;
  if (headers && data) {
    const csvData = convertToFile(headers, data);
    // @ts-expect-error - FIXME: Property 'msSaveBlob' does not exist on type 'Navigator'.ts(2339)
    if (navigator.msSaveBlob) {
      // @ts-expect-error - FIXME: Property 'msSaveBlob' does not exist on type 'Navigator'.ts(2339)
      navigator.msSaveBlob(csvData, filename);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(csvData);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const txnListMetrics = (acctTxns) => {
  let numTxns = 0;
  let sumTxns = 0;
  let earliestDate = DateTime.now();
  let reviewedCount = 0;
  acctTxns.forEach((section, _) => {
    numTxns += section.size;
    section.forEach((txn) => {
      sumTxns += Number(txn.amount);
      const postedOn = DateTime.fromISO(txn.postedOn);
      if (postedOn < earliestDate) {
        earliestDate = postedOn;
      }
      reviewedCount += (txn.isReviewed ? 1 : 0);
    });
  });

  return { numTxns, sumTxns, earliestDate, reviewedCount };
};

/**
 * This is a filter factory that builds a filtering function from
 * an expression. Lodash memoizer ensures that for the same expression
 * string, the same function instance is always returned. This allows
 * the function to be passed to a reselector and not bust the cache.
 *
 * This could be considered a leak in the sense that a function
 * will be preserved for each filter expression a user executes, but practically
 * that will be bounded by the duration of the session. The backing map
 * implementation could probably be swapped out if this turns out to
 * be an issue.
 */
export const createFilterFromExpression = lodash.memoize((expression) => {
  if (expression) {
    return (txns) => filterTransactionsByExpression(txns, expression);
  }
  return null;
});

export const createFilterFromFilterObject = lodash.memoize((filterObject) => {
  if (filterObject) {
    return (txns) => filterTransactionsByFilterObject(txns, filterObject);
  }
  return null;
});

const filterFromObjectAndExpression = (txns, filterObject, filterExpression) => {
  let newTxns = txns;
  if (newTxns && filterObject) {
    newTxns = filterTransactionsByFilterObject(newTxns, filterObject);
  }
  if (newTxns && filterExpression && filterExpression.length > 0) {
    newTxns = filterTransactionsByExpression(newTxns, filterExpression);
  }
  return newTxns;
};

export const createFilterFromObjectAndExpression = (filterObject, filterExpression) => {
  const cacheObject = {
    filterObject,
    filterExpression,
  };

  return memoizeWithObjectKey(
    'registerFilters',
    () => (txns) => filterFromObjectAndExpression(txns, filterObject, filterExpression),
    cacheObject,
    20,
  );
};
